import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import "./ManagePackage.css";
import EditIcon from "../../static/image/editIcon.svg";
import DeleteIcon from "../../static/image/deleteIcon.svg";
import PackageDetailEdit from "./PackageDetailEdit";
import CommonDeleteModal from "../../components/commonDeleteModal/CommonDeleteModal";
import CommonSuccessModal from "../../components/commonSuccessModal/CommonSuccessModal";

import {
  API_STATE,
  FORM_TYPE,
  LANGUAGE_DEFAULT_OPTION,
  LANGUAGE_OPTIONS,
  STATUS_CODE_RESPONSE_OK,
  ROUTES
} from "../../utils/constant";
import { deletePackages, fetchPackages } from "../../redux/packagesSlice";
import { isEmpty } from "../../utils/commonUtil";
import { decodedJWTPayload } from '../../utils/commonUtil';

function ManagePackage() {
  const navigate = useNavigate();
  const { packageId } = useParams();
  const location = useLocation();

  const dispatch = useDispatch();

  const {
    data: packageList,
    status,
    error,
  } = useSelector((state) => state.packageList);

  const [language, setLanguage] = useState(LANGUAGE_DEFAULT_OPTION);
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [isShowAlert, setIsShowAlert] = useState(null);
  const [isAlertSuccess, setIAlertSuccess] = useState(null);
  const [permission, setPermission] = useState('');

  const formType = useRef(null);

  const getEditModeFromRoute = () => {
    if (location.pathname.includes('/create')) return FORM_TYPE.CREATE;
    if (location.pathname.includes('/edit/')) return FORM_TYPE.EDIT;
    return undefined;
  };

  const [editMode, setEditMode] = useState(getEditModeFromRoute());

  useEffect(() => {
    dispatch(fetchPackages(language));

    const token = localStorage.getItem('access_token');
    if (token) {
      const payload = decodedJWTPayload(token);
      if (payload?.mn_id) {
        const modulePermission = payload.mn_id.split('|')
          .find(item => item.startsWith('020100-'));
        if (modulePermission) {
          setPermission(modulePermission.split('-')[1]);
        } else {
          setPermission('');
        }
      }
    }
  }, [dispatch, language]);

  useEffect(() => {
    const newEditMode = getEditModeFromRoute();
    setEditMode(newEditMode);
    formType.current = newEditMode;
  }, [location.pathname]);

  const toggleLanguage = (selectedLanguage) => {
    setLanguage(selectedLanguage);
    dispatch(fetchPackages(selectedLanguage));
  };

  const handleEdit = (id) => {
    if (permission.includes('W')) {
      navigate(`/dashboard/manage-package/edit/${id}`);
    }
  };

  const handleAddNew = () => {
    if (permission.includes('W')) {
      navigate(ROUTES.PACKAGE_CREATE);
    }
  };

  const handleResetPackages = () => {
    navigate(ROUTES.MANAGE_PACKAGE);
    dispatch(fetchPackages(language));
  };

  const handleBack = () => {
    navigate(ROUTES.MANAGE_PACKAGE);
  };

  const handleDeleteClick = (data) => {
    if (permission.includes('X')) {
      setPackageToDelete(data);
      setDeleteModalShow(true);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalShow(false);
    setPackageToDelete(null);
  };

  const handleDeleteConfirm = () => {
    if (packageToDelete && permission.includes('X')) {
      const response = dispatch(deletePackages(packageToDelete.uuid));
      response
        .then((res) => {
          if (res?.payload?.status === STATUS_CODE_RESPONSE_OK) {
            dispatch(fetchPackages(language));
          }
          if (res?.error) {
            handleAlertShow(res.error.message, false);
          }

          setDeleteModalShow(false);
          setPackageToDelete(null);
        })
        .catch((err) => {
          handleAlertShow(err, false);
          setDeleteModalShow(false);
          setPackageToDelete(null);
        });
    }
  };

  const handleAlertShow = (message, isSuccess) => {
    setIsShowAlert(message);
    setIAlertSuccess(isSuccess);
  };

  const handleAlertClose = () => {
    setIsShowAlert(null);
    setIAlertSuccess(null);
  };

  if (editMode) {
    return (
      <PackageDetailEdit
        packageId={packageId}
        formType={formType.current}
        initLanguage={language}
        handleComplete={handleResetPackages}
        handleBack={handleBack}
      />
    );
  }

  return (
    <div className="create-user-container">
      <div className="package-wrapper">
        <div
          className="package-language-container hidden-language-toggle"
          style={{ position: "relative", top: "20px" }}
        >
          <div className="package-language-toggle">
            {LANGUAGE_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => toggleLanguage(option.value)}
                className={`package-language-option ${language === option.value ? "package-language-active" : ""
                  }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="package-container">
          <div className="package-header">Package</div>

          {status === API_STATE.LOADING && (
            <div className="loading-packages">Loading packages...</div>
          )}

          {status === API_STATE.FAILED && (
            <div className="error-packages">
              Error loading packages: {error}
            </div>
          )}

          {status === API_STATE.SUCCEEDED && (
            <>
              {packageList.map((data, index) => (
                <div key={data.uuid} className={`package-item`}>
                  <div className="package-content">
                    <div className="package-info">
                      <div className="package-label">
                        Package name:&nbsp;
                        <span className="package-value">{data.name}</span>
                      </div>
                      <div className="package-label">
                        Package description:&nbsp;
                        <span className="package-value">
                          {data.description}
                        </span>
                      </div>
                    </div>
                    <div className="package-actions">
                      <button
                        className="icon-btn"
                        onClick={() => handleEdit(data.uuid)}
                        style={{
                          opacity: permission.includes('W') ? 1 : 0.5,
                          cursor: permission.includes('W') ? 'pointer' : 'default',
                          pointerEvents: permission.includes('W') ? 'auto' : 'none'
                        }}
                      >
                        <img
                          src={EditIcon}
                          alt="Edit"
                          className="action-icon"
                        />
                      </button>
                      <button
                        className="icon-btn"
                        onClick={() => handleDeleteClick(data)}
                        style={{
                          opacity: permission.includes('X') ? 1 : 0.5,
                          cursor: permission.includes('X') ? 'pointer' : 'default',
                          pointerEvents: permission.includes('X') ? 'auto' : 'none'
                        }}
                      >
                        <img
                          src={DeleteIcon}
                          alt="Delete"
                          className="action-icon"
                        />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {packageList.length === 0 && (
                <div className="empty-packages">
                  No packages found. Click "Add new" to create your first package.
                </div>
              )}
            </>
          )}
        </div>

        <button
          className="package-add-btn"
          onClick={handleAddNew}
          style={{
            opacity: permission.includes('W') ? 1 : 0.5,
            cursor: permission.includes('W') ? 'pointer' : 'default',
            pointerEvents: permission.includes('W') ? 'auto' : 'none'
          }}
        >
          Add new
        </button>
      </div>

      <CommonSuccessModal
        showModal={!isEmpty(isShowAlert)}
        handleClose={handleAlertClose}
        message={isShowAlert}
        status={isAlertSuccess}
      />

      <CommonDeleteModal
        show={deleteModalShow}
        onHide={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Package"
        instruction={`Are you sure you want to delete package "${packageToDelete?.name}"?`}
      />
    </div>
  );
}

export default ManagePackage;